/**
 * Zustand store for working representation state
 * Tracks new nodes, colors, and manual additions
 */

import { create } from 'zustand'
import { getColors } from '@/lib/theme'

export interface NodeMetadata {
  id: string
  text: string
  type: 'explicit' | 'deductive'
  color: string
  paletteIndex: number
  createdAt: number
  isNew: boolean // Fades to false after 3 seconds
}

export interface VizChunk {
  id: string
  text: string
  size: number
  type: 'explicit' | 'deductive'
}

interface RepresentationStore {
  // Node tracking
  nodeMetadata: Map<string, NodeMetadata>
  newNodeIds: Set<string>

  // Actions
  registerNode: (id: string, text: string, type: 'explicit' | 'deductive') => void
  isNodeNew: (id: string) => boolean
  getNodeColor: (id: string) => string | null
  getNodeType: (id: string) => 'explicit' | 'deductive' | null

  // Cleanup old "new" flags
  updateNewNodeFlags: () => void

  // Manual fact addition
  manuallyAddedFacts: Array<{ content: string; type: 'explicit' | 'deductive' }>
  addFact: (content: string, type: 'explicit' | 'deductive') => void

  // Visualization persistence
  processedChunkIds: Set<string>
  hasProcessedChunk: (id: string) => boolean
  addProcessedChunks: (ids: string[]) => void
  normalizationBounds: { minX: number; maxX: number; minY: number; maxY: number } | null
  getNormalizationBounds: () => { minX: number; maxX: number; minY: number; maxY: number } | null
  setNormalizationBounds: (b: { minX: number; maxX: number; minY: number; maxY: number }) => void

  // Embeddings and chunks persistence
  vizTextChunks: VizChunk[]
  setVizTextChunks: (chunks: VizChunk[]) => void
  appendVizTextChunks: (chunks: VizChunk[]) => void

  vizEmbeddingsHighD: number[][]
  setVizEmbeddingsHighD: (emb: number[][]) => void
  appendVizEmbeddingsHighD: (emb: number[][]) => void

  // Raw 2D projection space (pre-canvas normalization)
  vizEmbeddingsRaw2D: Array<[number, number]>
  setVizEmbeddingsRaw2DReplace: (points: Array<[number, number]>) => void
  appendVizEmbeddingsRaw2D: (points: Array<[number, number]>) => void

  vizEmbeddings2D: Array<[number, number]>
  setVizEmbeddings2DReplace: (points: Array<[number, number]>) => void
  appendVizEmbeddings2D: (points: Array<[number, number]>) => void

  // Clear all visualization state (for switching between full/contextual views)
  clearVizState: () => void
}

// Build a theme-derived palette each time we need a color
function getThemePalettes() {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  const c = getColors(isDark)
  const explicit = [
    c.visualAccent2,
    c.userMsgAccent || c.visualAccent1,
    c.successBorder,
    c.hoverAccent,
    c.primaryAccent,
  ].filter(Boolean) as string[]
  const deductive = [
    c.visualAccent3,
    c.visualAccent4,
    c.errorBorder,
    c.assistantMsgAccent || c.visualAccent1,
    c.navBorder,
  ].filter(Boolean) as string[]
  return { explicit, deductive }
}

export const useRepresentationStore = create<RepresentationStore>((set, get) => ({
  nodeMetadata: new Map(),
  newNodeIds: new Set(),
  manuallyAddedFacts: [],
  processedChunkIds: new Set(),
  normalizationBounds: null,
  vizTextChunks: [],
  vizEmbeddingsHighD: [],
  vizEmbeddingsRaw2D: [],
  vizEmbeddings2D: [],

  registerNode: (id, text, type) => {
    const { nodeMetadata } = get()

    // Don't re-register existing nodes
    if (nodeMetadata.has(id)) return

    // Pick color index deterministically from theme-derived palettes
    const { explicit, deductive } = getThemePalettes()
    const palette = type === 'explicit' ? explicit : deductive
    const paletteIndex = Math.floor(Math.random() * Math.max(1, palette.length))
    const color = palette[paletteIndex % palette.length]

    const metadata: NodeMetadata = {
      id,
      text,
      type,
      color,
      paletteIndex,
      createdAt: Date.now(),
      isNew: true,
    }

    const newMap = new Map(nodeMetadata)
    newMap.set(id, metadata)

    const newSet = new Set(get().newNodeIds)
    newSet.add(id)

    set({
      nodeMetadata: newMap,
      newNodeIds: newSet,
    })

    // Auto-remove "new" flag after 3 seconds
    setTimeout(() => {
      const { nodeMetadata, newNodeIds } = get()
      const updatedMeta = nodeMetadata.get(id)
      if (updatedMeta) {
        updatedMeta.isNew = false
        const newMap = new Map(nodeMetadata)
        newMap.set(id, updatedMeta)

        const newSet = new Set(newNodeIds)
        newSet.delete(id)

        set({
          nodeMetadata: newMap,
          newNodeIds: newSet,
        })
      }
    }, 3000)
  },

  isNodeNew: (id) => {
    return get().newNodeIds.has(id)
  },

  getNodeColor: (id) => {
    const metadata = get().nodeMetadata.get(id)
    if (!metadata) return null
    // Derive current color from theme using stored palette index
    const { explicit, deductive } = getThemePalettes()
    const palette = metadata.type === 'explicit' ? explicit : deductive
    if (palette.length > 0) {
      return palette[metadata.paletteIndex % palette.length]
    }
    return metadata.color || null
  },

  getNodeType: (id) => {
    const metadata = get().nodeMetadata.get(id)
    return metadata?.type || null
  },

  updateNewNodeFlags: () => {
    const { nodeMetadata, newNodeIds } = get()
    const now = Date.now()
    const updatedSet = new Set(newNodeIds)

    // Remove nodes that are older than 3 seconds
    Array.from(newNodeIds).forEach((id) => {
      const metadata = nodeMetadata.get(id)
      if (metadata && now - metadata.createdAt > 3000) {
        updatedSet.delete(id)
        metadata.isNew = false
      }
    })

    if (updatedSet.size !== newNodeIds.size) {
      set({ newNodeIds: updatedSet })
    }
  },

  addFact: (content, type) => {
    const { manuallyAddedFacts } = get()
    set({
      manuallyAddedFacts: [...manuallyAddedFacts, { content, type }],
    })

    // Dispatch event to trigger visualization update
    window.dispatchEvent(
      new CustomEvent('manual-fact-added', {
        detail: { content, type },
      })
    )
  },

  hasProcessedChunk: (id) => {
    return get().processedChunkIds.has(id)
  },

  addProcessedChunks: (ids) => {
    const setIds = new Set(get().processedChunkIds)
    ids.forEach((id) => setIds.add(id))
    set({ processedChunkIds: setIds })
  },

  getNormalizationBounds: () => {
    return get().normalizationBounds
  },

  setNormalizationBounds: (b) => {
    set({ normalizationBounds: b })
  },

  setVizTextChunks: (chunks) => {
    set({ vizTextChunks: chunks })
  },
  appendVizTextChunks: (chunks) => {
    set({ vizTextChunks: [...get().vizTextChunks, ...chunks] })
  },

  setVizEmbeddingsHighD: (emb) => {
    set({ vizEmbeddingsHighD: emb })
  },
  appendVizEmbeddingsHighD: (emb) => {
    set({ vizEmbeddingsHighD: [...get().vizEmbeddingsHighD, ...emb] })
  },

  setVizEmbeddingsRaw2DReplace: (points) => {
    set({ vizEmbeddingsRaw2D: points })
  },
  appendVizEmbeddingsRaw2D: (points) => {
    set({ vizEmbeddingsRaw2D: [...get().vizEmbeddingsRaw2D, ...points] })
  },

  setVizEmbeddings2DReplace: (points) => {
    set({ vizEmbeddings2D: points })
  },
  appendVizEmbeddings2D: (points) => {
    set({ vizEmbeddings2D: [...get().vizEmbeddings2D, ...points] })
  },

  clearVizState: () => {
    set({
      processedChunkIds: new Set(),
      vizTextChunks: [],
      vizEmbeddingsHighD: [],
      vizEmbeddingsRaw2D: [],
      vizEmbeddings2D: [],
      normalizationBounds: null,
    })
  },
}))
